import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaTrophy, FaUsers, FaCalendar, FaDollarSign, FaArrowRight, FaMedal, FaCrown, FaChartLine, FaHome, FaGraduationCap, FaComments, FaQuestionCircle, FaArrowLeft, FaWallet, FaFlask } from 'react-icons/fa';
import ChallengeService from '../services/ChallengeService';
import LeagueChallengesService from '../services/LeagueChallengesService';
import AuthService from '../services/AuthService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple } from '../utils/vfx';
import BottomNav from './BottomNav';
import './ChallengeDetails.css';

const ChallengeDetails = () => {
  const navigate = useNavigate();
  const { challengeId, leagueId } = useParams();
  const { state } = useUser();
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isParticipating, setIsParticipating] = useState(false);
  const [participation, setParticipation] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [joining, setJoining] = useState(false);
  const [isLeagueChallenge, setIsLeagueChallenge] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (!AuthService.isAuthenticated() || !state.user) {
        navigate('/login', { replace: true });
        return;
      }
      soundEffects.playPageTransition();
      await loadChallengeDetails();
    };
    checkAuth();
  }, [navigate, state.user, challengeId, leagueId]);

  const loadChallengeDetails = async () => {
    try {
      setLoading(true);
      let data;
      
      // Check if this is a league challenge
      if (leagueId && state.user?.leagueId === leagueId) {
        setIsLeagueChallenge(true);
        data = await LeagueChallengesService.getChallengeForStudent(leagueId, challengeId);
      } else {
        data = await ChallengeService.getChallenge(challengeId);
      }
      
      setChallenge(data.challenge);
      setLeaderboard(data.leaderboard || []);
      setIsParticipating(data.isParticipating);
      setParticipation(data.participation);
      setUserRank(data.userRank);
      setParticipantCount(data.participantCount || 0);
    } catch (error) {
      toast.error(error.message || 'Failed to load challenge details');
      navigate('/challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinChallenge = async () => {
    try {
      setJoining(true);
      await ChallengeService.joinChallenge(challengeId);
      toast.success('Successfully joined challenge!');
      await loadChallengeDetails();
    } catch (error) {
      console.error('Join challenge error:', error);
      const errorMessage = error.message || 'Failed to join challenge';
      toast.error(errorMessage);
    } finally {
      setJoining(false);
    }
  };

  const getLeagueLabel = (league) => {
    const labels = { '10k': '$10,000', '100k': '$100,000', '1M': '$1,000,000' };
    return labels[league] || league;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChallengeStatus = () => {
    if (!challenge) return 'unknown';
    const now = new Date();
    const start = new Date(challenge.startDate);
    const end = new Date(challenge.endDate);

    if (now < start) return 'upcoming';
    if (now > end) return 'past';
    if (challenge.isActive) return 'active';
    return 'inactive';
  };

  const getMedalIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  if (loading) {
    return (
      <div className="challenge-details-page">
        <div className="loading">Loading challenge details...</div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="challenge-details-page">
        <div className="error-state">
          <h2>Challenge not found</h2>
          <button onClick={() => navigate('/challenges')} className="btn-back">
            <FaArrowLeft />
          </button>
        </div>
      </div>
    );
  }

  const status = getChallengeStatus();
  const canJoin = status === 'active' && !isParticipating;

  return (
    <div className="challenge-details-page">
      <div className="challenge-details-header">
        <button 
          onClick={() => navigate('/challenges')} 
          className="back-button"
          onMouseEnter={() => soundEffects.playHover()}
        >
          <FaArrowLeft />
        </button>
        <div className="header-content">
          <h1><FaTrophy /> {challenge.title}</h1>
          <span className={`status-badge ${status}`}>
            {status === 'active' && '🟢 Active'}
            {status === 'upcoming' && '⏰ Upcoming'}
            {status === 'past' && '🏁 Ended'}
          </span>
        </div>
      </div>

      <div className="challenge-details-content">
        {/* Challenge Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="info-card"
        >
          {challenge.description && (
            <div className="description-section">
              <h3>Description</h3>
              <p>{challenge.description}</p>
            </div>
          )}

          <div className="info-grid">
            <div className="info-item">
              <FaDollarSign />
              <div>
                <span className="label">League</span>
                <span className="value">{getLeagueLabel(challenge.league)}</span>
              </div>
            </div>
            <div className="info-item">
              <FaUsers />
              <div>
                <span className="label">Participants</span>
                <span className="value">{participantCount}</span>
              </div>
            </div>
            <div className="info-item">
              <FaCalendar />
              <div>
                <span className="label">Start Date</span>
                <span className="value">{formatDate(challenge.startDate)}</span>
              </div>
            </div>
            <div className="info-item">
              <FaCalendar />
              <div>
                <span className="label">End Date</span>
                <span className="value">{formatDate(challenge.endDate)}</span>
              </div>
            </div>
            {challenge.prizePool && (
              <div className="info-item prize">
                <FaTrophy />
                <div>
                  <span className="label">Prize Pool</span>
                  <span className="value">{formatCurrency(challenge.prizePool)}</span>
                </div>
              </div>
            )}
            {challenge.startingBalance && (
              <div className="info-item">
                <FaDollarSign />
                <div>
                  <span className="label">Starting Balance</span>
                  <span className="value">{formatCurrency(challenge.startingBalance)}</span>
                </div>
              </div>
            )}
          </div>

          {canJoin && (
            <button
              onClick={(e) => {
                createRipple(e);
                handleJoinChallenge();
              }}
              disabled={joining}
              className="join-button"
            >
              {joining ? 'Joining...' : <><FaArrowRight /> Join Challenge</>}
            </button>
          )}

          {isParticipating && participation && (
            <div className="participation-card">
              <h3>Your Participation</h3>
              <div className="participation-stats">
                <div className="stat">
                  <span className="stat-label">Your Rank</span>
                  <span className="stat-value">#{userRank || 'N/A'}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Current Value</span>
                  <span className="stat-value">{formatCurrency(participation.currentValue)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Starting Value</span>
                  <span className="stat-value">{formatCurrency(participation.startingValue)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Change</span>
                  <span className={`stat-value ${parseFloat(participation.currentValue) >= parseFloat(participation.startingValue) ? 'positive' : 'negative'}`}>
                    {formatCurrency(parseFloat(participation.currentValue) - parseFloat(participation.startingValue))}
                  </span>
                </div>
              </div>
              {participation.portfolioId && (
                <button
                  onClick={(e) => {
                    createRipple(e);
                    navigate(`/portfolio?portfolioId=${participation.portfolioId}`);
                  }}
                  className="view-portfolio-button"
                >
                  <FaWallet /> View & Manage Portfolio
                </button>
              )}
            </div>
          )}
        </motion.div>

        {/* Leaderboard Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="leaderboard-card"
        >
          <h2><FaTrophy /> Leaderboard</h2>
          {leaderboard.length === 0 ? (
            <div className="empty-leaderboard">
              <p>No participants yet. Be the first to join!</p>
            </div>
          ) : (
            <div className="leaderboard-list">
              {leaderboard.map((entry, index) => {
                const rank = index + 1;
                const medal = getMedalIcon(rank);
                const isCurrentUser = entry.userId === state.user?.id;

                return (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`leaderboard-entry ${isCurrentUser ? 'current-user' : ''} ${rank <= 3 ? 'top-three' : ''}`}
                  >
                    <div className="rank">
                      {medal ? <span className="medal">{medal}</span> : <span className="rank-number">#{rank}</span>}
                    </div>
                    <div className="user-info">
                      <span className="username">{entry.name || entry.username || entry.email}</span>
                      {isCurrentUser && <span className="you-badge">You</span>}
                    </div>
                    <div className="value">
                      {formatCurrency(entry.currentValue)}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ChallengeDetails;

